import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutomationHelpersIntegrations } from 'app/automations/automation-helpers-integrations';
import { Automation } from 'app/automations/automations-models';
import { IntegrationSettings, IntegrationType } from 'app/automations/integrations-models';
import { Account } from 'app/back-office/account.model';
import { Widget, WidgetState } from 'app/campaign/widgets.model';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { IntegrationsService } from 'app/shared/services/integrations.service';
import { WidgetService } from 'app/shared/services/widget.service';
import { FlyoutService } from '../../flyout.service';

@Component({
  selector: 'app-flyout-form-settings-ecommerce',
  templateUrl: './flyout-form-settings-ecommerce.component.html',
  styleUrl: './flyout-form-settings-ecommerce.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutFormSettingsEcommerceComponent implements OnInit {
  @Input() public widget: Widget | null = null;
  @Input() public widgetState!: WidgetState;
  @Input() public account: Account | null = null;
  @Input() public existingAutomation: Automation | null = null;
  @Input() public campaignName: string | null = null;

  /** Emitted after the registration type changes, so the show-message section can re-gate the alternating-message toggle. */
  @Output() public readonly registrationChanged = new EventEmitter<void>();

  private readonly integrationsService = inject(IntegrationsService);
  private readonly widgetService = inject(WidgetService);
  private readonly widgetEditorDataService = inject(WidgetEditorDataService);
  private readonly flyoutService = inject(FlyoutService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  public eCommerceIntegration: IntegrationSettings | null = null;
  public saving = false;

  public ngOnInit(): void {
    this.integrationsService.activeIntegrationsBS
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((integrations) => this.resolveEcommerceIntegration(integrations ?? []));
  }

  /** Show when a real eCommerce integration is connected, or when a registration type is already saved
   *  (even if the integration has since been disabled). */
  public get visible(): boolean {
    if (!this.eCommerceIntegration) {
      return false;
    }
    return !this.eCommerceIntegration.isDisable || !!this.widget?.formRegistrationType;
  }

  public get isOn(): boolean {
    return !!this.widget?.formRegistrationType;
  }

  public onCardClick(): void {
    if (!this.isOn && !this.saving) {
      this.updateRegistrationType(true);
    }
  }

  // Stop the click from bubbling to the card header (which would re-toggle), then turn registration off.
  public onRemove(event: Event): void {
    event.stopPropagation();
    this.updateRegistrationType(false);
  }

  public updateRegistrationType(checked: boolean): void {
    const type = this.eCommerceIntegration?.type;
    if (!this.widget || !type) {
      return;
    }
    const value = checked ? type : undefined;
    this.saving = true;
    this.widgetService
      .patchWidget(this.widget.id, '/formRegistrationType', value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.widget) {
            this.widget.formRegistrationType = value;
          }
          // Reuse the editor's registration-automation flow (create or add/remove the real-time action).
          if (this.widgetState?.automationId) {
            this.widgetEditorDataService.updateAutomationWithRealTimeRegistrationType(
              this.widgetState.automationId,
              type,
              value,
            );
          } else if (value) {
            this.widgetEditorDataService.createAutomationWithRealTimeRegistrationType(
              type,
              this.campaignName,
              this.account,
              this.widgetState,
            );
          }
          if (value) {
            this.widgetEditorDataService.setDefaultFormSettingsOnWidget(this.existingAutomation, value);
          }
          this.flyoutService.flyoutFormSettingsBS.next({ ...this.widget, formRegistrationType: value });
          this.widgetService.hasUnpublishedChangesBS.next(true);
          this.registrationChanged.emit();
          this.saving = false;
          this.snackBarService.success('generic.success');
        },
        error: () => {
          this.saving = false;
          this.snackBarService.error('generic.errorOccurred');
        },
      });
  }

  private resolveEcommerceIntegration(integrations: IntegrationSettings[]): void {
    const centra = integrations.find((integration) => integration.type === IntegrationType.Centra);
    const shopify = integrations.find((integration) => integration.type === IntegrationType.Shopify);
    if (centra?.data?.checkoutApiUrl) {
      this.eCommerceIntegration = { ...centra, config: AutomationHelpersIntegrations.CENTRA_CONFIG };
    } else if (shopify?.data?.shopName) {
      this.eCommerceIntegration = { ...shopify, config: AutomationHelpersIntegrations.SHOPIFY_CONFIG };
    } else {
      this.eCommerceIntegration = {
        config: AutomationHelpersIntegrations.CENTRA_CONFIG,
        isDisable: true,
        type: IntegrationType.Centra,
      };
    }
  }
}
